import { BrowserRouter as Router, Route, Routes, NavLink, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const BitcoinDominance = () => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [high, setHigh] = useState(null);
  const [low, setLow] = useState(null);
  const [dominance, setDominance] = useState({ btc: null, eth: null, others: null });
  const [percentages, setPercentages] = useState({
    yesterday: null,
    lastWeek: null,
    lastMonth: null,
  });

  const options = {
    scales: {
      y: {
        ticks: { callback: v => `${v.toFixed(2)}%` },
        grid: { color: "rgba(0,0,0,0.06)" }
      },
      x: {
        grid: { display: false }
      }
    },
    plugins: {
      legend: { display: true },
      tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)}%` } }
    },
    elements: { point: { radius: 0 } },
    interaction: { intersect: false, mode: "index" }
  };

  useEffect(() => {
    const fetchDominance1 = async (coinId) => {
      try {
        const res = await fetch(`https://coinmediaworld.com/api/get-dominance?coinId=${coinId}&days=1`);
        const data = await res.json();

        setHigh(data.high && { price: data.high.value, date: new Date(data.high.timestamp) });
        setLow(data.low && { price: data.low.value, date: new Date(data.low.timestamp) });

        return Array.isArray(data?.dominance) ? data.dominance : [];
      } catch (e) {
        console.error("Failed to fetch dominance:", e);
        return [];
      }
    };

    const fetchData = async () => {
      try {
        const [btcDom, ethDom, adaDom] = await Promise.all([
          fetchDominance1("bitcoin"),
          fetchDominance1("ethereum"),
          fetchDominance1("cardano"),
        ]);

        // labels: HH:mm since it's intraday
        const labels = btcDom.map(([ts]) => {
          const d = new Date(ts);
          return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        });

        setChartData({
          labels,
          datasets: [
            {
              label: "BTC Dominance (%)",
              data: btcDom.map(([, p]) => p),
              borderColor: "#16a34a",
              backgroundColor: "rgba(22,163,74,0.15)",
              fill: true,
              tension: 0.35,
            },
            {
              label: "ETH Dominance (%)",
              data: ethDom.map(([, p]) => p),
              borderColor: "#3b82f6",
              backgroundColor: "rgba(59,130,246,0.12)",
              fill: true,
              tension: 0.35,
            },
            {
              label: "ADA Dominance (%)",
              data: adaDom.map(([, p]) => p),
              borderColor: "#a0aec0",
              backgroundColor: "rgba(160,174,192,0.12)",
              fill: true,
              tension: 0.35,
            },
          ],
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const fetchPrices = async () => {
      const res = await fetch('https://coinmediaworld.com/api/get-historical-data?coinId=bitcoin');
      const data = await res.json();
      const prices = data.prices; // [timestamp, price]

      const now = prices[prices.length - 1][1];
      const yesterday = prices[prices.length - 2][1]; // 1 day ago (approx 24h)
      const lastWeek = prices[Math.floor(prices.length * 7 / 30)][1];
      const lastMonth = prices[0][1];

      const format = (value) => `${(value * 100).toFixed(1)}%`;

      setPercentages({
        yesterday: format(yesterday / now),
        lastWeek: format(lastWeek / now),
        lastMonth: format(lastMonth / now),
      });
    };

    const fetchDominance = async () => {
      try {
        const res = await fetch('https://coinmediaworld.com/api/get-global-data');
        const data1 = await res.json();
        const btc = data1.data.market_cap_percentage.btc;
        const eth = data1.data.market_cap_percentage.eth;
        const others = 100 - btc - eth;

        setDominance({ btc, eth, others });
      } catch (err) {
        console.error("Failed to fetch dominance data", err);
      }
    };

    fetchDominance();
    fetchPrices();
    fetchData();
  }, []);

  const formatDate = (date) =>
    date?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });

  const formatPercent = (value) => (value ? `${value.toFixed(1)}%` : '—');

  const format = (val) => val !== null ? `${val.toFixed(1)}%` : '—';
  
  if (loading) return <p>Loading chart...</p>;
  
  return (
    <div className="dominance_mainbox">
		<div className="dominancechart_box">
			<div className="dominancebox">
				<h3>Bitcoin Dominance</h3>
				
				<div className="dominanceinfo">
					<span>Bitcoin:</span><strong style={{ color: "#f7931a" }}>{format(dominance.btc)}</strong>
				</div>
				
				<div className="dominanceinfo">
					<span>Ethereum:</span><strong style={{ color: "#627eea" }}>{format(dominance.eth)}</strong>
				</div>
				
				<div className="dominanceinfo">
					<span>Others:</span><strong style={{ color: "#718096" }}>{format(dominance.others)}</strong>
				</div>
			</div>

			<div className="dominancechart">
				<h3>Bitcoin Dominance Chart</h3>
				<Line data={chartData} options={options}/>
			</div>
		</div>
    {/*
		<div className="dominance_valuebox">
			<div className="values_box">
				<h4>Historical Values</h4>
				<p>Yesterday: <strong style={{ color: "#f7931a" }}>{percentages.yesterday || '—'}</strong></p>
				<p>Last Week: <strong style={{ color: "#f7931a" }}>{percentages.lastWeek || '—'}</strong></p>
				<p>Last Month: <strong style={{ color: "#f7931a" }}>{percentages.lastMonth || '—'}</strong></p>
			</div>

			<div className="values_box">
				<h4>Yearly High and Low</h4>
				<p>High ({formatDate(high?.date)}):{' '} <strong style={{ color: "#f7931a" }}>{formatPercent(high?.price / low?.price * 100)}</strong></p>
				<p>Low ({formatDate(low?.date)}):{' '} <strong style={{ color: "#f7931a" }}>{formatPercent(100)}</strong></p>
			</div>
		</div>
    */}
    </div>
  );
};

export default BitcoinDominance;
